import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FiPlus, FiTrash2, FiEdit2, FiX, FiCheck, FiCalendar, FiUser, FiBriefcase, FiFlag, FiSearch } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import {
  getTodos, createTodo, updateTodo, toggleTodoComplete, deleteTodo,
  getTeamMembers, getJobs
} from '../services/api';

const PRIORITY_OPTIONS = ['Urgent', 'High', 'Normal', 'Low'];

const PRIORITY_STYLE = {
  Urgent: { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
  High:   { bg: '#fff7ed', color: '#ea580c', border: '#fed7aa' },
  Normal: { bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe' },
  Low:    { bg: '#f9fafb', color: '#6b7280', border: '#e5e7eb' },
};

function formatDueDate(dateStr) {
  if (!dateStr) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr + 'T00:00:00');
  const diffDays = Math.round((d - today) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return { label: 'Today', tone: 'today' };
  if (diffDays === 1) return { label: 'Tomorrow', tone: 'soon' };
  if (diffDays < 0) return { label: `${Math.abs(diffDays)}d overdue`, tone: 'overdue' };
  if (diffDays < 7) return { label: d.toLocaleDateString('en-US', { weekday: 'short' }), tone: 'soon' };
  return { label: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), tone: 'later' };
}

const DUE_TONE_STYLE = {
  overdue: { bg: '#fef2f2', color: '#b91c1c' },
  today:   { bg: '#fff7ed', color: '#c2410c' },
  soon:    { bg: '#eff6ff', color: '#1d4ed8' },
  later:   { bg: '#f3f4f6', color: '#4b5563' },
};

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function Todos() {
  const { user } = useAuth();
  const isManager = ['Admin', 'Owner', 'Office Manager'].includes(user?.role);

  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState('open');           // 'open' | 'completed' | 'all'
  const [view, setView] = useState('mine');           // 'mine' | 'assigned_by_me'
  const [showForm, setShowForm] = useState(false);
  const [editingTodo, setEditingTodo] = useState(null);
  const [team, setTeam] = useState([]);
  const [error, setError] = useState(null);

  const blankForm = () => ({
    text: '',
    notes: '',
    due_date: '',
    priority: 'Normal',
    owner_id: user?.id || '',
    related_job_id: '',
    // Kept alongside the id purely so the picker can show what's chosen without
    // re-fetching the job just to learn its number.
    related_job_label: '',
  });
  const [form, setForm] = useState(blankForm());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { show };
      if (view === 'assigned_by_me' && isManager) {
        params.include_assigned_by_me = 1;
      }
      const res = await getTodos(params);
      const list = res.data.todos || [];
      const filtered = view === 'assigned_by_me'
        ? list.filter(t => t.assigned_by_id === user.id && t.owner_id !== user.id)
        : list.filter(t => t.owner_id === user.id);
      setTodos(filtered);
    } catch (e) {
      setError('Failed to load todos');
    }
    setLoading(false);
  }, [show, view, isManager, user?.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!isManager) return;
    getTeamMembers().then(r => {
      const list = r.data?.team_members || (Array.isArray(r.data) ? r.data : []);
      setTeam(list.filter(m => m.is_active));
    }).catch(() => {});
  }, [isManager]);

  const openCreate = () => {
    setEditingTodo(null);
    setForm(blankForm());
    setShowForm(true);
  };

  const openEdit = (todo) => {
    setEditingTodo(todo);
    setForm({
      text: todo.text || '',
      notes: todo.notes || '',
      due_date: todo.due_date || '',
      priority: todo.priority || 'Normal',
      owner_id: todo.owner_id || user.id,
      related_job_id: todo.related_job_id || '',
      related_job_label: todo.related_job_number || '',
    });
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e?.preventDefault();
    if (!form.text.trim()) return;
    try {
      const payload = {
        text: form.text.trim(),
        notes: form.notes || null,
        due_date: form.due_date || null,
        priority: form.priority || 'Normal',
        related_job_id: form.related_job_id || null,
      };
      if (isManager) payload.owner_id = form.owner_id || user.id;
      if (editingTodo) {
        await updateTodo(editingTodo.id, payload);
      } else {
        await createTodo(payload);
      }
      setShowForm(false);
      setEditingTodo(null);
      load();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to save');
    }
  };

  const handleToggle = async (todo) => {
    try {
      await toggleTodoComplete(todo.id, !todo.completed);
      load();
    } catch (e) {
      alert('Failed to update');
    }
  };

  const handleDelete = async (todo) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await deleteTodo(todo.id);
      load();
    } catch (e) {
      alert('Failed to delete');
    }
  };

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '20px 16px' }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '16px', flexWrap: 'wrap', gap: '12px'
      }}>
        <h1 style={{ margin: 0, fontSize: '24px', color: '#111827' }}>
          {view === 'assigned_by_me' ? 'Assigned by Me' : 'My To-Do'}
        </h1>
        <button
          onClick={openCreate}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '8px 14px', backgroundColor: '#2563eb', color: 'white',
            border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          <FiPlus size={16} /> New Task
        </button>
      </div>

      {/* Filter tabs */}
      <div style={{
        display: 'flex', gap: '6px', marginBottom: '14px', flexWrap: 'wrap'
      }}>
        {isManager && (
          <div style={{ display: 'flex', gap: '6px', marginRight: '12px' }}>
            <FilterPill active={view === 'mine'} onClick={() => setView('mine')}>Mine</FilterPill>
            <FilterPill active={view === 'assigned_by_me'} onClick={() => setView('assigned_by_me')}>Assigned by Me</FilterPill>
          </div>
        )}
        <FilterPill active={show === 'open'} onClick={() => setShow('open')}>Open</FilterPill>
        <FilterPill active={show === 'completed'} onClick={() => setShow('completed')}>Completed</FilterPill>
        <FilterPill active={show === 'all'} onClick={() => setShow('all')}>All</FilterPill>
      </div>

      {error && (
        <div style={{ padding: '12px', backgroundColor: '#fef2f2', color: '#b91c1c', borderRadius: '8px', marginBottom: '12px' }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
      ) : todos.length === 0 ? (
        <div style={{
          padding: '40px 20px', textAlign: 'center', color: '#9ca3af',
          backgroundColor: '#f9fafb', borderRadius: '12px', border: '1px dashed #e5e7eb'
        }}>
          {show === 'open' ? 'Nothing on the list. Nicely done.' : 'No tasks here.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {todos.map(t => (
            <TodoRow
              key={t.id}
              todo={t}
              showOwner={view === 'assigned_by_me'}
              onToggle={() => handleToggle(t)}
              onEdit={() => openEdit(t)}
              onDelete={() => handleDelete(t)}
            />
          ))}
        </div>
      )}

      {showForm && (
        <TodoForm
          form={form}
          setForm={setForm}
          onSave={handleSave}
          onClose={() => { setShowForm(false); setEditingTodo(null); }}
          isManager={isManager}
          team={team}
          editingTodo={editingTodo}
          currentUserId={user?.id}
        />
      )}
    </div>
  );
}

function FilterPill({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '6px 14px', fontSize: '13px',
        borderRadius: '999px',
        border: active ? '1px solid #2563eb' : '1px solid #e5e7eb',
        backgroundColor: active ? '#eff6ff' : 'white',
        color: active ? '#1d4ed8' : '#4b5563',
        fontWeight: active ? 600 : 500,
        cursor: 'pointer'
      }}
    >
      {children}
    </button>
  );
}

function TodoRow({ todo, showOwner, onToggle, onEdit, onDelete }) {
  const due = formatDueDate(todo.due_date);
  const dueStyle = due ? DUE_TONE_STYLE[due.tone] : null;
  const priorityStyle = PRIORITY_STYLE[todo.priority] || PRIORITY_STYLE.Normal;

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: '12px',
      padding: '12px 14px',
      backgroundColor: 'white',
      border: '1px solid #e5e7eb',
      borderLeft: `4px solid ${priorityStyle.color}`,
      borderRadius: '10px',
      opacity: todo.completed ? 0.55 : 1,
    }}>
      <button
        onClick={onToggle}
        aria-label={todo.completed ? 'Mark incomplete' : 'Mark complete'}
        style={{
          flexShrink: 0, marginTop: '2px',
          width: '22px', height: '22px',
          borderRadius: '6px',
          border: `2px solid ${todo.completed ? '#16a34a' : '#d1d5db'}`,
          backgroundColor: todo.completed ? '#16a34a' : 'white',
          color: 'white',
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}
      >
        {todo.completed && <FiCheck size={14} />}
      </button>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: '14px', fontWeight: 500, color: '#111827',
          textDecoration: todo.completed ? 'line-through' : 'none',
          wordBreak: 'break-word'
        }}>
          {todo.text}
        </div>
        {todo.notes && (
          <div style={{ marginTop: '4px', fontSize: '13px', color: '#6b7280', whiteSpace: 'pre-wrap' }}>
            {todo.notes}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px', flexWrap: 'wrap' }}>
          {due && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '4px',
              fontSize: '11px', fontWeight: 600,
              padding: '2px 8px', borderRadius: '999px',
              backgroundColor: dueStyle.bg, color: dueStyle.color,
            }}>
              <FiCalendar size={10} /> {due.label}
            </span>
          )}
          {todo.priority && todo.priority !== 'Normal' && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '4px',
              fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
              padding: '2px 8px', borderRadius: '4px',
              backgroundColor: priorityStyle.bg, color: priorityStyle.color,
              border: `1px solid ${priorityStyle.border}`,
            }}>
              <FiFlag size={10} /> {todo.priority}
            </span>
          )}
          {todo.related_job_id && todo.related_job_number && (
            <Link to={`/jobs/${todo.related_job_id}`} style={{
              display: 'inline-flex', alignItems: 'center', gap: '4px',
              fontSize: '11px', fontWeight: 600,
              padding: '2px 8px', borderRadius: '4px',
              backgroundColor: '#f5f3ff', color: '#5b21b6',
              textDecoration: 'none',
            }}>
              <FiBriefcase size={10} /> {todo.related_job_number}
            </Link>
          )}
          {showOwner && todo.owner_name && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '4px',
              fontSize: '11px', fontWeight: 600,
              padding: '2px 8px', borderRadius: '4px',
              backgroundColor: '#ecfdf5', color: '#047857',
            }}>
              <FiUser size={10} /> {todo.owner_name}
            </span>
          )}
          {!showOwner && todo.assigned_by_name && (
            <span style={{ fontSize: '11px', color: '#9ca3af' }}>
              from {todo.assigned_by_name}
            </span>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
        <button
          onClick={onEdit}
          aria-label="Edit"
          style={{
            background: 'none', border: 'none', padding: '6px',
            color: '#6b7280', cursor: 'pointer', borderRadius: '6px'
          }}
        >
          <FiEdit2 size={15} />
        </button>
        <button
          onClick={onDelete}
          aria-label="Delete"
          style={{
            background: 'none', border: 'none', padding: '6px',
            color: '#ef4444', cursor: 'pointer', borderRadius: '6px'
          }}
        >
          <FiTrash2 size={15} />
        </button>
      </div>
    </div>
  );
}

const jobLabel = (j) => [j.job_number, j.client_name || j.property_address].filter(Boolean).join(' — ');

// Type-to-search picker for "Related job". This used to be a <select> holding
// `jobs.slice(0, 200)` — with 2,200+ jobs that silently made most of them unpickable, and
// scrolling 200 options to find one was hopeless anyway. Searching runs on the server
// (job number, address, client, county, subdivision…), so every job is reachable.
function JobPicker({ valueId, valueLabel, onPick }) {
  const [term, setTerm] = useState('');
  const [hits, setHits] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounce = useRef();

  const search = (text) => {
    clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      setLoading(true);
      try {
        const t = text.trim();
        // No text yet: show the most recent jobs, so the list is useful before typing.
        const qs = t ? `search=${encodeURIComponent(t)}&limit=15` : 'limit=15';
        const r = await getJobs(qs);
        const list = r.data?.jobs || r.data || [];
        setHits(Array.isArray(list) ? list : []);
      } catch (e) {
        setHits([]);
      }
      setLoading(false);
    }, 220);
  };

  // Clean up a pending search if the form closes mid-keystroke.
  useEffect(() => () => clearTimeout(debounce.current), []);

  if (valueId) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
        border: '1px solid #d1d5db', borderRadius: 8, backgroundColor: '#f9fafb',
      }}>
        <FiBriefcase size={14} style={{ color: '#2563eb', flexShrink: 0 }} />
        <span style={{ flex: 1, minWidth: 0, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {valueLabel || `Job #${valueId}`}
        </span>
        <button
          type="button"
          onClick={() => { onPick('', ''); setTerm(''); setHits([]); setOpen(false); }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: 2, flexShrink: 0 }}
          title="Clear the related job"
        >
          <FiX size={15} />
        </button>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <FiSearch size={14} style={{
          position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af',
        }} />
        <input
          type="text"
          value={term}
          placeholder="Search by job number, address, or client…"
          onChange={(e) => { setTerm(e.target.value); setOpen(true); search(e.target.value); }}
          onFocus={() => { setOpen(true); if (hits.length === 0) search(term); }}
          // Delay so a click on a result lands before the list unmounts.
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          style={{
            width: '100%', padding: '8px 10px 8px 30px', fontSize: '14px',
            border: '1px solid #d1d5db', borderRadius: '8px', boxSizing: 'border-box',
          }}
        />
      </div>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, marginTop: 4,
          background: 'white', border: '1px solid #e5e7eb', borderRadius: 8,
          boxShadow: '0 6px 18px rgba(0,0,0,0.12)', maxHeight: 240, overflowY: 'auto',
        }}>
          {loading && hits.length === 0 ? (
            <div style={{ padding: '9px 11px', fontSize: 13, color: '#6b7280' }}>Searching…</div>
          ) : hits.length === 0 ? (
            <div style={{ padding: '9px 11px', fontSize: 13, color: '#6b7280' }}>
              {term.trim() ? 'No jobs match that.' : 'No jobs found.'}
            </div>
          ) : (
            hits.map((j, i) => (
              <button
                key={j.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { onPick(j.id, jobLabel(j)); setOpen(false); setTerm(''); }}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', padding: '8px 11px',
                  background: 'none', border: 'none', cursor: 'pointer', fontSize: 13,
                  borderTop: i ? '1px solid #f1f5f9' : 'none',
                }}
              >
                <span style={{ fontWeight: 600 }}>{j.job_number}</span>
                {(j.client_name || j.property_address) && (
                  <span style={{ display: 'block', color: '#6b7280', fontSize: 12, marginTop: 1 }}>
                    {[j.client_name, j.property_address].filter(Boolean).join(' · ')}
                  </span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function TodoForm({ form, setForm, onSave, onClose, isManager, team, editingTodo, currentUserId }) {
  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 99990
      }} />
      <div role="dialog" aria-modal="true" style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: 'min(560px, 92vw)', maxHeight: '88vh',
        backgroundColor: 'white', borderRadius: '14px',
        boxShadow: '0 20px 50px rgba(0,0,0,0.25)', zIndex: 99995,
        display: 'flex', flexDirection: 'column', overflow: 'hidden'
      }}>
        <div style={{
          padding: '14px 18px', borderBottom: '1px solid #e5e7eb',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <h2 style={{ margin: 0, fontSize: '18px' }}>{editingTodo ? 'Edit Task' : 'New Task'}</h2>
          <button onClick={onClose} aria-label="Close" style={{
            background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '4px'
          }}>
            <FiX size={20} />
          </button>
        </div>
        <form onSubmit={onSave} style={{ padding: '16px 18px', overflowY: 'auto' }}>
          <Field label="What needs to be done?" required>
            <input
              type="text"
              value={form.text}
              onChange={(e) => setForm(f => ({ ...f, text: e.target.value }))}
              required
              autoFocus
              maxLength={500}
              style={inputStyle}
              placeholder="Call the county about parcel records"
            />
          </Field>

          <Field label="Notes (optional)">
            <textarea
              value={form.notes}
              onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
              rows={3}
              style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
            />
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <Field label="Due date">
              <input
                type="date"
                value={form.due_date}
                onChange={(e) => setForm(f => ({ ...f, due_date: e.target.value }))}
                style={inputStyle}
              />
              <div style={{ marginTop: '4px', fontSize: '12px' }}>
                <button type="button" onClick={() => setForm(f => ({ ...f, due_date: todayISO() }))} style={quickDateBtn}>Today</button>
                <button type="button" onClick={() => {
                  const d = new Date(); d.setDate(d.getDate() + 1);
                  setForm(f => ({ ...f, due_date: `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}` }));
                }} style={quickDateBtn}>Tomorrow</button>
                <button type="button" onClick={() => setForm(f => ({ ...f, due_date: '' }))} style={quickDateBtn}>None</button>
              </div>
            </Field>

            <Field label="Priority">
              <select
                value={form.priority}
                onChange={(e) => setForm(f => ({ ...f, priority: e.target.value }))}
                style={inputStyle}
              >
                {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
          </div>

          {isManager && team.length > 0 && (
            <Field label="Assign to">
              <select
                value={form.owner_id || currentUserId}
                onChange={(e) => setForm(f => ({ ...f, owner_id: parseInt(e.target.value, 10) }))}
                style={inputStyle}
              >
                <option value={currentUserId}>Me</option>
                {team.filter(m => m.id !== currentUserId).map(m => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </Field>
          )}

          <Field label="Related job (optional)">
            <JobPicker
              valueId={form.related_job_id}
              valueLabel={form.related_job_label}
              onPick={(id, label) => setForm(f => ({ ...f, related_job_id: id, related_job_label: label }))}
            />
          </Field>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
            <button type="button" onClick={onClose} style={{
              padding: '8px 16px', fontSize: '14px', backgroundColor: 'white',
              border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer', color: '#374151'
            }}>Cancel</button>
            <button type="submit" style={{
              padding: '8px 16px', fontSize: '14px', backgroundColor: '#2563eb',
              border: '1px solid #2563eb', borderRadius: '8px', cursor: 'pointer', color: 'white', fontWeight: 600
            }}>{editingTodo ? 'Save' : 'Create Task'}</button>
          </div>
        </form>
      </div>
    </>
  );
}

function Field({ label, required, children }) {
  return (
    <div style={{ marginBottom: '12px' }}>
      <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
        {label}{required && <span style={{ color: '#dc2626' }}> *</span>}
      </label>
      {children}
    </div>
  );
}

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  fontSize: '14px',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  outline: 'none',
  boxSizing: 'border-box',
};

const quickDateBtn = {
  background: 'none',
  border: 'none',
  color: '#2563eb',
  cursor: 'pointer',
  fontSize: '12px',
  marginRight: '6px',
  padding: 0,
};

export default Todos;
